import { useCallback } from 'react';
import axiosInstance from '../../../axiosInstance';

/**
 * Custom hook for previewing files
 *
 * SUPPORTED PREVIEW TYPES (Backend Limitations):
 * - Images: JPEG, PNG, GIF (native browser support)
 * - Documents: PDF, Text files (native browser support)
 *
 * IMPORTANT: Microsoft Office files (PPT, PPTX, DOC, DOCX, XLS, XLSX) are NOT supported
 * for preview by the backend API. These files can only be downloaded.
 *
 * @returns {object} - { previewFile, previewing, error, fileInfo, previewUrl, clearPreview, isPreviewSupported }
 */
export const useFilePreview = () => {
  /**
   * Preview a file by ID.
   *
   * Fetches the file as a blob via axiosInstance (authenticated) and opens
   * the resulting Object URL in a new tab.  This ensures STAGED files pass
   * the ownership check because the JWT token is included in the request.
   *
   * @param {number|string} fileId - The file ID to preview
   * @param {string} fileName - The file name to display in case of error
   */
  const previewFile = useCallback(async (fileId, fileName) => {
    try {
      if (
        !fileId ||
        fileId === 'undefined' ||
        fileId === 'null' ||
        fileId === 'NaN'
      ) {
        console.error('Invalid file ID for preview:', fileId);
        alert(`Invalid file ID for preview: ${fileName || 'unknown file'}`);
        return;
      }

      // Handle staged- prefix (legacy compatibility)
      let actualFileId = fileId;
      if (typeof fileId === 'string' && fileId.startsWith('staged-')) {
        actualFileId = fileId.slice('staged-'.length);
      }

      const endpoint = `/api/v1/files/${actualFileId}/preview`;

      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error opening file preview:', error);
      alert(`Could not open preview for ${fileName || `file ${fileId}`}`);
    }
  }, []);

  /**
   * Check if a file type is supported for preview
   * @param {string} mimeType - The MIME type to check
   * @returns {object} - Detailed support information
   * @returns {boolean} returns.isSupported - Whether file type is supported by backend for upload
   * @returns {boolean} returns.isPreviewable - Whether file can be previewed in browser (ONLY images, PDFs, text)
   * @returns {boolean} returns.isCompressed - Whether file is a compressed archive
   * @returns {boolean} returns.isOfficeFile - Whether file is a Microsoft Office document
   * @returns {boolean} returns.recommendDownload - Whether download is recommended over preview
   */
  const isPreviewSupported = useCallback((mimeType) => {
    if (!mimeType)
      return {
        isSupported: false,
        isPreviewable: false,
        isCompressed: false,
        isOfficeFile: false,
        recommendDownload: false,
      };

    const previewableTypes = [
      // Images (native browser support) - CONFIRMED BY API
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',

      // Documents (native browser support) - CONFIRMED BY API
      'application/pdf',
      'text/plain',

      // Additional text types commonly supported by browsers
      'text/csv',
      'text/html',
      'text/xml',
      'text/css',
      'text/javascript',
      'application/json',
    ];

    // Microsoft Office files - NOT SUPPORTED BY BACKEND API (download only)
    const officeTypes = [
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    ];

    // Compressed files are supported for upload but not for preview
    const compressedTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.rar',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/gzip',
      'application/x-bzip2',
      'application/x-xz',
    ];

    const normalizedMimeType = mimeType.toLowerCase();

    // Check if it's a previewable type (only images and PDFs/text)
    const isPreviewable = previewableTypes.some((type) =>
      normalizedMimeType.includes(type)
    );

    // Check if it's a compressed file
    const isCompressed = compressedTypes.some((type) =>
      normalizedMimeType.includes(type)
    );

    // Check if it's an Office file
    const isOfficeFile = officeTypes.some((type) =>
      normalizedMimeType.includes(type)
    );

    return {
      isSupported: isPreviewable || isCompressed || isOfficeFile,
      isPreviewable: isPreviewable, // Only true for images, PDFs, and text files
      isCompressed: isCompressed,
      isOfficeFile: isOfficeFile,
      recommendDownload: isCompressed || isOfficeFile, // Office files also recommend download
    };
  }, []);

  /**
   * Simple boolean check for backward compatibility
   * @param {string} mimeType - The MIME type to check
   * @returns {boolean} - Whether the file can be previewed
   */
  const canPreviewFile = useCallback(
    (mimeType) => {
      const support = isPreviewSupported(mimeType);
      return support?.isPreviewable || false;
    },
    [isPreviewSupported]
  );

  return {
    // Actions
    previewFile,

    // File type checking
    isPreviewSupported, // Returns detailed object
    canPreviewFile, // Returns simple boolean
  };
};

export default useFilePreview;
